// NOVAPAY-116-R5A — shared commerce UI primitives (one family for checkout / pay / paid / FOP / COD).
import type { ReactNode } from "react";

export function CommercePageShell({
  children,
  wide = false,
  testId,
}: {
  children: ReactNode;
  wide?: boolean;
  testId?: string;
}) {
  return (
    <div
      className={`sv2-commerce-shell${wide ? " sv2-commerce-shell--wide" : ""}`}
      data-testid={testId ?? "sv2-commerce-shell"}
    >
      {children}
    </div>
  );
}

export function StatusHeroCard({
  title,
  status,
  body,
  children,
  phase,
}: {
  title: string;
  status?: string;
  body?: string;
  children?: ReactNode;
  phase?: string;
}) {
  return (
    <section className="sv2-status-hero" data-testid="sv2-status-hero" data-phase={phase}>
      <h2>{title}</h2>
      {status && status !== title ? <p className="sv2-status-hero-status">{status}</p> : null}
      {body ? <p className="sv2-muted">{body}</p> : null}
      {children}
    </section>
  );
}

export function ContentCard({
  title,
  children,
  testId,
}: {
  title?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="sv2-content-card" data-testid={testId}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </section>
  );
}

export function ActionGrid({ children }: { children: ReactNode }) {
  return (
    <div className="sv2-action-grid" data-testid="sv2-action-grid">
      {children}
    </div>
  );
}

export function ActionTile({
  title,
  hint,
  onClick,
  disabled,
  testId,
}: {
  title: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className="sv2-action-tile"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="sv2-action-tile-title">{title}</span>
      {hint ? <span className="sv2-action-tile-hint">{hint}</span> : null}
    </button>
  );
}

export function PrimaryCommerceAction({
  children,
  onClick,
  disabled,
  loading,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className="sv2-cta sv2-primary-commerce"
      data-testid={testId}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function SecondaryCommerceAction({
  children,
  onClick,
  disabled,
  loading,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className="sv2-cta sv2-secondary sv2-action-secondary"
      data-testid={testId}
      data-sv2-btn-variant="secondary"
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function IconCommerceAction({
  children,
  onClick,
  disabled,
  label,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className="sv2-qty-btn sv2-icon-commerce"
      data-testid={testId}
      disabled={disabled}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function TertiaryTextAction({
  children,
  onClick,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button type="button" className="sv2-about-link sv2-tertiary-action" data-testid={testId} onClick={onClick}>
      {children}
    </button>
  );
}
